import React, {
  useEffect,
  useRef,
  useCallback,
  useContext,
  createContext,
} from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormGroup,
  Checkbox,
  IconButton,
  Paper,
  MenuItem,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Formik, FieldArray, useFormikContext } from "formik";
import { useAnswers, useSaveAnswers } from "apiRequests/useAnswers";
import StepNav from "@/components/StepNav";

// ---------------------------------------------------------------------------
// Question content, transcribed from "2025 - AdCom Intake Questionnaire".
// Each panel is rendered as its OWN Formik form (see Section below) so typing
// in one section only re-renders that section, not the whole questionnaire.
// ---------------------------------------------------------------------------

type Q = [name: string, label: string, multiline?: boolean];

const backgroundQuestions: Q[] = [
  ["homeLanguage", "What language do you speak at home?"],
  ["residenceDuration", "How long have you lived in your current location?"],
  ["moveFrequency", "How often have you moved?"],
  ["brothers", "How many siblings do you have?"],
  ["pets", "Do you own any pets (if so, what, and for how long)?"],
  ["instruments", "What instruments do you play? (And how long?)"],
  ["sportsPlayed", "What sports do you play? (And how many years?)"],
  ["freeTime", "What do you like to do in your free time?", true],
  [
    "readNonAssigned",
    "How often do you read books that were not assigned for school?",
  ],
  ["bookTypes", "What types of books do you enjoy reading?"],
  [
    "sportsFan",
    "Are you a sports fan? If so, what are your favorite sports to follow, and which teams?",
    true,
  ],
  ["favoriteMovieStar", "Who is your favorite movie star?"],
  ["favoriteBand", "What is your favorite band/singing group?"],
  ["favoriteMovie", "What is your favorite movie, and why?", true],
  ["favoriteBook", "What is your favorite book, and why?", true],
  ["museumVisits", "How often do you visit museums?"],
  ["enjoyTraveling", "Do you enjoy traveling?"],
  [
    "languagesSpoken",
    "What languages do you speak? (include your level of proficiency)",
    true,
  ],
  ["religion", "What is your religious affiliation?"],
  ["travelFrequency", "How often do you travel?"],
  ["placesTraveled", "Where have you traveled?", true],
  ["dreamDestination", "If you could travel anywhere, where would you go?"],
  ["summers", "What do you generally do over the summers?", true],
  ["favoriteSubject", "What is your favorite subject in school, and why?", true],
  [
    "professionalAspiration",
    "What do you aspire to do professionally, and why?",
    true,
  ],
  ["homeResponsibilities", "What are your responsibilities at home?", true],
  ["parentsOccupations", "What are your parents/guardians’ occupations?", true],
  ["parentsDegrees", "What are your parents’ educational degrees?"],
  ["driveToSchool", "Do you drive to school?"],
  ["friendsActivities", "What do you like to do with your friends?", true],
  ["hoursStudyPerDay", "How many hours per day do you study?"],
  ["socialMediaHours", "How many hours a day do you spend on social media?"],
  ["newspaper", "Do you read the news?"],
];

const geoRegions = [
  ["northeast", "Northeast"],
  ["midAtlantic", "Mid-Atlantic"],
  ["south", "South"],
  ["midwest", "Midwest"],
  ["mountainsSouthwest", "Mountains/Southwest"],
  ["westCoast", "West Coast"],
];

const settingOptions = ["Urban", "Suburban", "Rural"];
const sizeOptions = [
  "Very Small (under 1,500 students)",
  "Small (1,500-3,500)",
  "Medium (3,500-6,000)",
  "Large (6,000-15,000)",
  "Very Large (15,000-35,000+)",
];
const coedOptions = ["Coeducational", "Single-Sex", "Doesn’t Matter"];

const frequencyScale = [
  "Never or Hardly Ever",
  "Rarely (once or twice a year)",
  "Sometimes (3-4 times a year)",
  "Very Often (5+ times a year)",
];
const participationRows = [
  "See a movie at the movie theater",
  "Visit a museum or art gallery",
  "Attend a music concert",
  "Attend an opera, ballet, play or symphony",
  "Attend a sporting event (not your own athletic participation)",
  "Visit a zoo, science center, or aquarium",
];

const booksInHomeOptions = [
  "None or very few (0-10)",
  "Enough to fill 1 shelf (11-25)",
  "Enough to fill 1 bookcase (26-100)",
  "Enough to fill 2 bookcases (101-200)",
  "Enough to fill 3 or more bookcases (more than 200)",
];

const libraryFrequencyOptions = [
  "Never or very rarely",
  "A few times a year",
  "About once a month",
  "Several times a month",
];

const skillScale = ["Very Low", "Low", "Moderate", "High", "Very High"];
const skillRows = [
  "Remembering factual knowledge (terminology, classifications, methods, trends)",
  "Understanding fundamental concepts, principles, or theories",
  "Finding and using resources for answering questions or solving problems",
  "Applying knowledge, concepts, principles, or theories to a specific situation or problem",
  "Analyzing ideas, arguments, and points of view",
  "Synthesizing and integrating information",
  "Formulating questions in a specific field",
  "Conducting inquiry through the methods and paradigms of a specific field",
  "Evaluating the merits or value of ideas and competing claims",
  "Explaining the meaning of information",
  "Oral expression",
  "Writing skills",
];

const agreementScale = ["Agree", "Disagree"];
const agreementRows = [
  "I know the areas where I am academically weak and I try to improve them.",
  "I know what I want to be doing 10 years from now.",
  "I often make lists of things to do.",
  "My family has always wanted me to go to college.",
  "I usually mark important dates on my calendar.",
  "If tutoring is made available at college at no cost, I would attend regularly.",
  "I have talked about my career goals with someone who has worked in that field.",
  "On the whole, I am satisfied with myself.",
  "I try to find opportunities to learn new things.",
  "I have studied things about my major field (or favorite subject) on my own.",
];

const importanceScale = [
  "1 - Extremely",
  "2 - Very",
  "3 - Somewhat",
  "4 - Not at All",
];
const identityRows = [
  "Being a good athlete",
  "Being a politically active person",
  "Being a volunteer",
  "Your racial or ethnic identity",
  "Being a good student",
  "Being someone who socializes well with others",
  "Your religious affiliation",
  "Your gender",
];
const expectationRows = [
  "Meaningful social relationships",
  "Career preparation",
  "Personal growth/awareness",
  "Meeting people from different cultures and backgrounds",
  "Spiritual development",
  "Social and community responsibility",
  "Academic/intellectual skills",
  "Learning about my cultural heritage",
  "Learning to do things on my own",
  "Managing emotions and behaviors",
  "Academic/intellectual achievement",
  "Leadership skills",
];

const activityRow = {
  activity: "",
  freshman: false,
  sophomore: false,
  junior: false,
  senior: false,
  leadership: "",
  rank: "",
};

const getIn = (obj: any, path: string) =>
  path.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);

// Default value for each top-level key that isn't a plain string.
const groupDefaults: Record<string, any> = {
  geoRank: {},
  participationFrequency: {},
  skillLevels: {},
  agreementStatements: {},
  identityImportance: {},
  expectationsImportance: {},
  currentCourses: [],
  schoolActivities: [],
  schoolSports: [],
  outsideActivities: [],
};

// Build a section's initialValues from saved answers, scoped to its own keys.
const sectionInitial = (keys: string[], iq: any) =>
  Object.fromEntries(
    keys.map((k) => {
      const saved = iq?.[k];
      if (saved !== undefined) return [k, saved];
      const dflt = groupDefaults[k];
      if (Array.isArray(dflt)) return [k, [...dflt]];
      if (dflt !== undefined) return [k, { ...dflt }];
      return [k, ""];
    })
  );

const Panel: React.FC<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
    <Typography variant="h6" fontWeight="600" gutterBottom>
      {title}
    </Typography>
    {subtitle && (
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

// Render helpers, bound to a single form's Formik state.
const makeHelpers = (values: any, handleChange: any, setFieldValue: any) => {
  const text = ([name, label, multiline]: Q) => (
    <Grid item xs={12} sm={multiline ? 12 : 6} key={name}>
      <TextField
        fullWidth
        name={name}
        label={label}
        value={getIn(values, name) ?? ""}
        onChange={handleChange}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
      />
    </Grid>
  );

  const radio = (name: string, label: string, options: string[]) => (
    <Grid item xs={12} key={name}>
      <Typography fontWeight="500" gutterBottom>
        {label}
      </Typography>
      <RadioGroup
        row
        name={name}
        value={getIn(values, name) ?? ""}
        onChange={handleChange}
      >
        {options.map((o) => (
          <FormControlLabel key={o} value={o} control={<Radio />} label={o} />
        ))}
      </RadioGroup>
    </Grid>
  );

  const matrix = (group: string, rows: string[], scale: string[]) => (
    <Box sx={{ overflowX: "auto" }}>
      {rows.map((row, i) => {
        const path = `${group}.${i}`;
        return (
          <Box key={i} sx={{ py: 3.5, borderBottom: "1px solid #eee" }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {row}
            </Typography>
            <RadioGroup
              row
              name={path}
              value={getIn(values, path) ?? ""}
              onChange={handleChange}
            >
              {scale.map((s) => (
                <FormControlLabel
                  key={s}
                  value={s}
                  control={<Radio size="small" />}
                  label={<Typography variant="caption">{s}</Typography>}
                />
              ))}
            </RadioGroup>
          </Box>
        );
      })}
    </Box>
  );

  const rowArray = (
    name: string,
    fields: { key: string; label: string; type?: string }[],
    addLabel = "Add"
  ) => (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <>
          {(getIn(values, name) || []).map((row: any, i: number) => (
            <Grid
              container
              spacing={2}
              key={i}
              sx={{ mb: 1 }}
              alignItems="center"
            >
              {fields.map((f) => (
                <Grid item xs={12} sm key={f.key}>
                  <TextField
                    fullWidth
                    label={f.label}
                    type={f.type || "text"}
                    InputLabelProps={
                      f.type === "date" ? { shrink: true } : undefined
                    }
                    name={`${name}.${i}.${f.key}`}
                    value={row[f.key] ?? ""}
                    onChange={handleChange}
                  />
                </Grid>
              ))}
              <Grid item xs="auto">
                <IconButton color="error" onClick={() => remove(i)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button
            startIcon={<AddIcon />}
            onClick={() =>
              push(Object.fromEntries(fields.map((f) => [f.key, ""])))
            }
          >
            {addLabel}
          </Button>
        </>
      )}
    </FieldArray>
  );

  const activityTable = (name: string) => (
    <FieldArray name={name}>
      {({ push, remove }) => (
        <>
          {(getIn(values, name) || []).length === 0 && (
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              None added yet.
            </Typography>
          )}
          {(getIn(values, name) || []).map((row: any, i: number) => (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }} key={i}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Activity"
                    name={`${name}.${i}.activity`}
                    value={row.activity ?? ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm>
                  <Typography variant="caption">Years involved</Typography>
                  <FormGroup row>
                    {["freshman", "sophomore", "junior", "senior"].map((y) => (
                      <FormControlLabel
                        key={y}
                        control={
                          <Checkbox
                            size="small"
                            name={`${name}.${i}.${y}`}
                            checked={!!row[y]}
                            onChange={handleChange}
                          />
                        }
                        label={
                          <Typography
                            variant="caption"
                            sx={{ textTransform: "capitalize" }}
                          >
                            {y}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Leadership / Responsibility"
                    name={`${name}.${i}.leadership`}
                    value={row.leadership ?? ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={8} sm={1}>
                  <TextField
                    fullWidth
                    label="Rank"
                    name={`${name}.${i}.rank`}
                    value={row.rank ?? ""}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs="auto">
                  <IconButton color="error" onClick={() => remove(i)}>
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}
          <Button startIcon={<AddIcon />} onClick={() => push({ ...activityRow })}>
            Add Activity
          </Button>
        </>
      )}
    </FieldArray>
  );

  return { text, radio, matrix, rowArray, activityTable };
};

type Helpers = ReturnType<typeof makeHelpers>;

// After saving a section, move the user to the first field of the next one.
const focusFirstField = (containerId?: string) => {
  if (!containerId) return;
  const el = document.getElementById(containerId);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
  const field = el.querySelector<HTMLElement>(
    "input:not([type=hidden]), textarea, select"
  );
  field?.focus({ preventScroll: true });
};

// Lets the page-level "Save & Next" submit every section form at once, and
// signals (via bulkRef) that focus should NOT jump while doing so.
type QCtx = {
  register: (id: string, submit: () => Promise<any>) => void;
  bulkRef: React.MutableRefObject<boolean>;
};
const QuestionnaireContext = createContext<QCtx | null>(null);

// Registers its form's submitForm with the page so a bulk save can drive it.
const SubmitRegistrar: React.FC<{ id: string }> = ({ id }) => {
  const ctx = useContext(QuestionnaireContext);
  const { submitForm } = useFormikContext();
  useEffect(() => {
    ctx?.register(id, submitForm);
  }, [ctx, id, submitForm]);
  return null;
};

// A single panel = a single, independent Formik form.
const Section: React.FC<{
  id: string;
  nextId?: string;
  title: string;
  subtitle?: string;
  initialValues: any;
  onSave: (values: any) => Promise<any>;
  children: (helpers: Helpers, form: any) => React.ReactNode;
}> = ({ id, nextId, title, subtitle, initialValues, onSave, children }) => {
  const ctx = useContext(QuestionnaireContext);
  return (
    <div id={id}>
      <Panel title={title} subtitle={subtitle}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={(values, { setSubmitting }) =>
            Promise.resolve(onSave(values))
              .then(() => {
                // During a bulk "Save & Next", don't yank focus around.
                if (!ctx?.bulkRef.current) focusFirstField(nextId);
              })
              .finally(() => setSubmitting(false))
          }
        >
          {({ values, handleChange, setFieldValue, handleSubmit, isSubmitting }: any) => {
            const helpers = makeHelpers(values, handleChange, setFieldValue);
            return (
              <form onSubmit={handleSubmit}>
                <SubmitRegistrar id={id} />
                {children(helpers, { values, handleChange, setFieldValue })}
                <Box mt={2} display="flex" justifyContent="flex-end">
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    Save
                  </Button>
                </Box>
              </form>
            );
          }}
        </Formik>
      </Panel>
    </div>
  );
};

const backgroundKeys = backgroundQuestions.map((q) => q[0]);
const collegeKeys = [
  "geoRank",
  "setting",
  "collegeSize",
  "coed",
  "atmosphereIncorporate",
  "atmosphereAvoid",
  "prestigeImportance",
  "workloadIntensity",
  "intendedMajor",
  "possibleMajors",
  "academicInterests",
  "interestedColleges",
  "ruledOutColleges",
  "needFinancialAid",
  "wantPartTimeJob",
];
const academicKeys = [
  "currentCourses",
  "psat10",
  "psat11",
];
const extracurricularKeys = [
  "schoolActivities",
  "schoolSports",
  "outsideActivities",
];
const resourcesKeys = [
  "participationFrequency",
  "booksInHome",
  "libraryFrequency",
  "skillLevels",
];
const statementsKeys = ["agreementStatements"];
const aboutKeys = ["identityImportance", "expectationsImportance"];

export default function InitialQuestionnaire() {
  const { data, refetch, isReady } = useAnswers();
  const saveAnswers = useSaveAnswers();

  // Source of truth for the merged initialQuestions object. Updated
  // synchronously on every section save so back-to-back saves can't race,
  // and resynced from the server whenever fresh answers arrive.
  const latest = useRef<any>({});
  useEffect(() => {
    latest.current = data?.initialQuestions || {};
  }, [data]);

  // Each section registers its submitForm here so "Save & Next" can drive them.
  const submitters = useRef<Record<string, () => Promise<any>>>({});
  const bulkRef = useRef(false);
  const register = useCallback(
    (id: string, submit: () => Promise<any>) => {
      submitters.current[id] = submit;
    },
    []
  );

  if (!isReady) return <p>Loading...</p>;

  const iq = data?.initialQuestions || {};

  // Merge a section's own keys into the latest known object, then persist.
  const saveSection = (values: any) => {
    const merged = { ...latest.current, ...values };
    latest.current = merged;
    return saveAnswers({ initialQuestions: merged }).then((results) => {
      refetch();
      return results;
    });
  };

  // Submit every section in order, awaiting each (the server's read-modify-write
  // isn't atomic, so saving sequentially avoids lost updates). This also flushes
  // any section the user edited but forgot to Save.
  const saveAll = async () => {
    bulkRef.current = true;
    try {
      for (const id of Object.keys(submitters.current).sort()) {
        await submitters.current[id]();
      }
    } finally {
      bulkRef.current = false;
    }
    return true;
  };

  return (
    <QuestionnaireContext.Provider value={{ register, bulkRef }}>
    <Box p={4}>
      <Typography variant="h3" gutterBottom>
        Initial Client Questionnaire
      </Typography>

      <Section
        id="iq-section-0"
        nextId="iq-section-1"
        title="Background"
        initialValues={sectionInitial(backgroundKeys, iq)}
        onSave={saveSection}
      >
        {(h) => <Grid container spacing={2} rowSpacing={5}>{backgroundQuestions.map(h.text)}</Grid>}
      </Section>

      <Section
        id="iq-section-1"
        nextId="iq-section-2"
        title="College Preferences"
        subtitle="What type of college do you see yourself in? Review the following characteristics and your current priorities before answering."
        initialValues={sectionInitial(collegeKeys, iq)}
        onSave={saveSection}
      >
        {(h, f) => (
          <>
            <Typography fontWeight="500" gutterBottom>
              Please indicate where you prefer to attend college, ranking from
              1 being highest, to 5 being lowest.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {geoRegions.map(([key, label]) => (
                <Grid item xs={6} sm={3} key={key}>
                  <TextField
                    select
                    fullWidth
                    label={label}
                    name={`geoRank.${key}`}
                    value={getIn(f.values, `geoRank.${key}`) ?? ""}
                    onChange={f.handleChange}
                  >
                    <MenuItem value="">—</MenuItem>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <MenuItem key={n} value={String(n)}>
                        {n}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={2} rowSpacing={5}>
              <Grid item xs={12}>
                <Typography fontWeight="500" gutterBottom>
                  Setting
                </Typography>
                <TextField
                  select
                  fullWidth
                  name="setting"
                  value={getIn(f.values, "setting") ?? ""}
                  onChange={f.handleChange}
                >
                  <MenuItem value="">—</MenuItem>
                  {settingOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Typography fontWeight="500" gutterBottom>
                  Size of Total College/University
                </Typography>
                <TextField
                  select
                  fullWidth
                  name="collegeSize"
                  value={getIn(f.values, "collegeSize") ?? ""}
                  onChange={f.handleChange}
                >
                  <MenuItem value="">—</MenuItem>
                  {sizeOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <Typography fontWeight="500" gutterBottom>
                  Coeducational / Single-Sex
                </Typography>
                <TextField
                  select
                  fullWidth
                  name="coed"
                  value={getIn(f.values, "coed") ?? ""}
                  onChange={f.handleChange}
                >
                  <MenuItem value="">—</MenuItem>
                  {coedOptions.map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {h.text(["atmosphereIncorporate", "What are five things you're looking for in a college/university?", true])}
              {h.text(["atmosphereAvoid", "What are five things you want to avoid in a college/university?", true])}
              {h.text(["prestigeImportance", "How important a consideration is institutional prestige?"])}
              {h.text(["workloadIntensity", "How intensive an academic workload do you want?"])}
              {h.text(["intendedMajor", "What is your intended major?"])}
              {h.text(["possibleMajors", "What are your possible majors, if undecided?"])}
              {h.text(["academicInterests", "Academic interests apart from your major?", true])}
              {h.text(["interestedColleges", "Are there any colleges you are particularly interested in at this time? Please tell us why.", true])}
              {h.text(["ruledOutColleges", "Have you ruled out any colleges? Please tell us why.", true])}
              {h.text(["needFinancialAid", "Will you need financial aid?"])}
              {h.text(["wantPartTimeJob", "Do you want a part-time job?"])}
            </Grid>
          </>
        )}
      </Section>

      <Section
        id="iq-section-2"
        nextId="iq-section-3"
        title="Academic Profile"
        initialValues={sectionInitial(academicKeys, iq)}
        onSave={saveSection}
      >
        {(h) => (
          <>
            <Typography fontWeight="500" gutterBottom>
              Current courses and approximate grades
            </Typography>
            {h.rowArray(
              "currentCourses",
              [
                { key: "course", label: "Course" },
                { key: "grade", label: "Approx. Grade" },
              ],
              "Add Course"
            )}

            <Typography fontWeight="500" sx={{ mt: 3 }} gutterBottom>
              PSAT
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {h.text(["psat10", "PSAT (10th Grade)"])}
              {h.text(["psat11", "PSAT (11th Grade)"])}
            </Grid>
          </>
        )}
      </Section>

      <Section
        id="iq-section-3"
        nextId="iq-section-4"
        title="Extracurricular Activities"
        subtitle="List your most important extracurricular pursuits. Specify years of involvement, positions of leadership or responsibility."
        initialValues={sectionInitial(extracurricularKeys, iq)}
        onSave={saveSection}
      >
        {(h) => (
          <>
            <Typography fontWeight="500" gutterBottom>
              School Activities
            </Typography>
            {h.activityTable("schoolActivities")}

            <Typography fontWeight="500" sx={{ mt: 3 }} gutterBottom>
              School Sports
            </Typography>
            {h.activityTable("schoolSports")}

            <Typography fontWeight="500" sx={{ mt: 3 }} gutterBottom>
              Outside Activities (include outside sports)
            </Typography>
            {h.activityTable("outsideActivities")}
          </>
        )}
      </Section>

      <Section
        id="iq-section-4"
        nextId="iq-section-7"
        title="Activities & Resources"
        subtitle="How often do you participate in the following activities, either in or outside of school?"
        initialValues={sectionInitial(resourcesKeys, iq)}
        onSave={saveSection}
      >
        {(h) => (
          <>
            {h.matrix("participationFrequency", participationRows, frequencyScale)}

            <Box sx={{ mt: 3 }}>
              {h.radio(
                "booksInHome",
                "How many books are there in your home? (Do not include magazines.)",
                booksInHomeOptions
              )}
            </Box>
            <Box>
              {h.radio(
                "libraryFrequency",
                "How often do you borrow books from a public or school library?",
                libraryFrequencyOptions
              )}
            </Box>

            <Typography fontWeight="500" sx={{ mt: 3 }} gutterBottom>
              Please indicate your level of skill with the following:
            </Typography>
            {h.matrix("skillLevels", skillRows, skillScale)}
          </>
        )}
      </Section>

      <Section
        id="iq-section-7"
        nextId="iq-section-8"
        title="Statements"
        subtitle="Please indicate the extent to which you agree or disagree."
        initialValues={sectionInitial(statementsKeys, iq)}
        onSave={saveSection}
      >
        {(h) => h.matrix("agreementStatements", agreementRows, agreementScale)}
      </Section>

      <Section
        id="iq-section-8"
        title="About You and Your Friends"
        subtitle="Think about your overall self-identity. How important are each of the following sub-identities to your overall identity?"
        initialValues={sectionInitial(aboutKeys, iq)}
        onSave={saveSection}
      >
        {(h) => (
          <>
            {h.matrix("identityImportance", identityRows, importanceScale)}

            <Typography fontWeight="500" sx={{ mt: 3 }} gutterBottom>
              How important is each of these expectations to you?
            </Typography>
            {h.matrix("expectationsImportance", expectationRows, importanceScale)}
          </>
        )}
      </Section>

      <StepNav onSave={saveAll} />
    </Box>
    </QuestionnaireContext.Provider>
  );
}
